import { parseEther } from 'viem';
import { ensMessage, fullName, isAddress, priceOf } from '../../domain/ens/ens';
import { ApiResult, EnsApi, EnsWallet } from './ports';

/** Where the ETH for paid names goes; the API checks payments against the same address. */
export const PAYMENT_ADDRESS = '0xeD85dd7540b916d909641645d96c738D9e7d0873';

export type MintStep = 'checking' | 'signing' | 'paying' | 'minting';
export type Failure =
  | { ok: false; reason: 'no-wallet' | 'no-name' | 'taken' | 'rejected' | 'already-free' | 'bad-address' | 'not-image' | 'too-big' }
  | { ok: false; reason: 'failed'; message: string };

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

const isRejection = (e: unknown) => {
  const err = e as { name?: string; message?: string; code?: number };
  return err?.name === 'UserRejectedRequestError' || err?.code === 4001 || /rejected|denied/i.test(err?.message || '');
};

const failed = (e: unknown): Failure => (isRejection(e) ? { ok: false, reason: 'rejected' } : { ok: false, reason: 'failed', message: (e as Error)?.message || 'Something went wrong' });

const fromApi = (r: ApiResult): Failure => ({ ok: false, reason: 'failed', message: r.details || r.error || 'Something went wrong' });

export async function mintName({
  label,
  ethUsd,
  api,
  wallet,
  now = new Date(),
  onStep = () => {},
}: {
  label: string;
  ethUsd: number;
  api: EnsApi;
  wallet: EnsWallet;
  now?: Date;
  onStep?: (step: MintStep) => void;
}): Promise<{ ok: true; name: string } | Failure> {
  const address = wallet.address;
  if (!address) return { ok: false, reason: 'no-wallet' };
  if (!label) return { ok: false, reason: 'no-name' };
  try {
    onStep('checking');
    if (!(await api.isAvailable(label))) return { ok: false, reason: 'taken' };
    const price = priceOf(label, ethUsd);
    let result: ApiResult;
    if (price.usd === 0) {
      onStep('signing');
      const issuedAt = now.toISOString();
      const signature = await wallet.signMessage(ensMessage({ action: 'mint', name: fullName(label), value: address.toLowerCase(), issuedAt }));
      onStep('minting');
      result = await api.mint({ label, address, issuedAt, signature });
    } else {
      onStep('paying');
      const txHash = await wallet.pay(PAYMENT_ADDRESS, parseEther(price.eth.toFixed(6)));
      onStep('minting');
      result = await api.mint({ label, address, txHash });
    }
    if (result.ok) return { ok: true, name: fullName(label) };
    if (result.status === 403) return { ok: false, reason: 'already-free' };
    return fromApi(result);
  } catch (e) {
    return failed(e);
  }
}

export async function changeAddress({
  label,
  newAddress,
  api,
  wallet,
  now = new Date(),
}: {
  label: string;
  newAddress: string;
  api: EnsApi;
  wallet: EnsWallet;
  now?: Date;
}): Promise<{ ok: true } | Failure> {
  const address = wallet.address;
  if (!address) return { ok: false, reason: 'no-wallet' };
  if (!isAddress(newAddress)) return { ok: false, reason: 'bad-address' };
  try {
    const issuedAt = now.toISOString();
    const signature = await wallet.signMessage(ensMessage({ action: 'update-address', name: fullName(label), value: newAddress.toLowerCase(), issuedAt }));
    const r = await api.updateAddress({ label, newAddress, currentAddress: address, issuedAt, signature });
    return r.ok ? { ok: true } : fromApi(r);
  } catch (e) {
    return failed(e);
  }
}

export async function changeAvatar({
  label,
  file,
  api,
  wallet,
  now = new Date(),
}: {
  label: string;
  file: Blob;
  api: EnsApi;
  wallet: EnsWallet;
  now?: Date;
}): Promise<{ ok: true } | Failure> {
  const address = wallet.address;
  if (!address) return { ok: false, reason: 'no-wallet' };
  if (!file.type.startsWith('image/')) return { ok: false, reason: 'not-image' };
  if (file.size > MAX_AVATAR_BYTES) return { ok: false, reason: 'too-big' };
  try {
    const avatarUrl = await api.uploadAvatar(file, address);
    const issuedAt = now.toISOString();
    const signature = await wallet.signMessage(ensMessage({ action: 'update-avatar', name: fullName(label), value: avatarUrl, issuedAt }));
    const r = await api.updateAvatar({ label, currentAddress: address, avatarUrl, issuedAt, signature });
    return r.ok ? { ok: true } : fromApi(r);
  } catch (e) {
    return failed(e);
  }
}
